import { useState } from "react";
import { toast } from "react-toastify";
import { Request } from "../../../hooks/Request";
import { requestAuth } from "../../../components/services/RequestService";
import { Button } from "../../../components/Buttons";
import { NewProcedure, SendProcedure } from "../../../containers/steps";
import { useModal } from "../../../contexts/ModalContext";
import { useNavigate, useParams } from "react-router-dom";
import CallServiceTable from "../../../components/Tables/CallServiceTable";
import { useDialog } from "../../../contexts/DialogContext";
import { QuoteComparison } from "../../../components/common/QuoteComparison";
import { PurchaseOrder } from "../../../components/common/PurchaseOrder";
import { FormQuotes } from "../../../containers/ManegeProcedure/FormQuotes";
import { FormAdditionalInformation } from "../../../containers/ManegeProcedure/FormAdditionalInformation";
import { ComparativeTransportPdf } from "../../../components/common/ComparativeTransportPdf";
import { FormTransportContract } from "../../../containers/ManegeProcedure/FormTransportContract";
import { FormArrivalTracking } from "../../../containers/ManegeProcedure/FormArrivalTracking";
import { FormIncidents } from "../../../containers/ManegeProcedure/FormIncidents";
import { FormInsuranceApplication } from "../../../containers/ManegeProcedure/FormInsuranceApplication";
import { FormPaymentControl } from "../../../containers/ManegeProcedure/FormPaymentControl";
import { FormArriboImportacion } from "../../../containers/ManegeProcedure/FormArriboImportacion";
// import { ReportDatesModal } from "./ReportDatesModal";
import { ReportPaymentsModal } from "./ReportPaymentsModal";
import { ReportRitexModal } from "./ReportRitexModal";
import { ReportArrivalModal } from "./ReportArrivalModal";
import { ReportIncidentsModal } from "./ReportIncidentsModal";
import ViewSubProcedure from "../../../containers/ManegeProcedure/ViewSubProcedure";
import { FormNacionalizacion } from "../../../containers/ManegeProcedure/FormNacionalizacion";
import { ImportTrackingModal } from "../../../containers/ManegeProcedure/ImportTrackingModal";
import { useEffect } from "react";
import { InsuranceApplicationPDF } from "../../../containers/ManegeProcedure/InsuranceApplicationPDF";

const normalizeTransportMode = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

const isAirTransportProcedure = (procedure) =>
  normalizeTransportMode(procedure?.a13ParteModalidadDeTransporte) === "AEREO";

const subProceduresCache = new Map();

const SubProceduresCount = ({ procedureId }) => {
  const [state, setState] = useState(() => {
    const cached = subProceduresCache.get(procedureId);
    return cached
      ? { loading: false, error: null, count: cached.length }
      : { loading: true, error: null, count: 0 };
  });

  useEffect(() => {
    if (subProceduresCache.has(procedureId)) return;

    let cancelled = false;

    requestAuth("get", `/Procedure/${procedureId}/sub-procedures`)
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : [];
        subProceduresCache.set(procedureId, data);
        setState({ loading: false, error: null, count: data.length });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ loading: false, error: true, count: 0 });
      });

    return () => {
      cancelled = true;
    };
  }, [procedureId]);

  if (state.loading) {
    return <span className="text-xs text-gray-400">Cargando...</span>;
  }
  if (state.error) {
    return <span className="text-xs text-red-400">-</span>;
  }

  return (
    <span
      className={`flex items-center justify-center fke gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border m-1 ${
        state.count === 0
          ? "bg-gray-50 text-gray-500 border-gray-200"
          : "bg-blue-50 text-blue-700 border-blue-200"
      }`}
    >
      {state.count > 0 && (
        <span className="flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-xs font-semibold">
          {state.count}
        </span>
      )}
      {state.count === 0 ? "s/n" : ""}
    </span>
  );
};

const FolderList = () => {
  const { rolId } = useParams();
  const navigate = useNavigate();
  const { openModal, onClose } = useModal();
  const { openDialog, dialogClose } = useDialog();

  function handleAdd(reload) {
    openModal(<NewProcedure reload={reload} onClose={onClose} />);
  }

  function handleSendProcedure(procedure, reloadTable) {
    openModal(
      <SendProcedure
        openModal={openModal}
        procedure={procedure}
        reload={typeof reloadTable === "function" ? reloadTable : () => {}}
        onClose={onClose}
      />,
    );
  }

  function handleFolderStep(procedure) {
    navigate(`/rol/${rolId}/menu/folderList/step-procedure/${procedure.id}`);
  }
  function handleViewSubProcedure(procedure, reload) {
    openModal(
      <ViewSubProcedure rolId={rolId} procedure={procedure} reload={reload} />,
    );
  }

  function handleQuoteComparison(procedure) {
    openDialog(<QuoteComparison procedureId={procedure.id} />);
  }

  function handleSecure(procedure) {
    openDialog(<InsuranceApplicationPDF procedureId={procedure.id} />);
  }

  function handlePurchaseOrder(procedure) {
    openDialog(<PurchaseOrder procedureId={procedure.id} />);
  }

  function handleFormQuotes(procedure) {
    openModal(<FormQuotes procedure={procedure} />);
  }

  function handleArrivalTracking(procedure) {
    openModal(<FormArrivalTracking procedure={procedure} />);
  }

  function handleAdditionalInformation(procedure) {
    openModal(<FormAdditionalInformation procedure={procedure} />);
  }

  function handlePaymentControl(procedure) {
    openModal(<FormPaymentControl procedure={procedure} />);
  }

  function handleComparativeTransport(procedure) {
    openDialog(<ComparativeTransportPdf procedureId={procedure.id} />);
  }

  function handleTransportContract(procedure) {
    openModal(<FormTransportContract procedure={procedure} />);
  }

  function handleIncidents(procedure) {
    openDialog(<FormIncidents procedure={procedure} />);
  }
  function handleSureApplication(procedure, reloadTable) {
    openModal(
      <FormInsuranceApplication
        procedure={procedure}
        reload={reloadTable}
        handleSendProcedure={handleSendProcedure}
      />,
    );
  }

  function handleArriboImportacion(procedure) {
    openModal(<FormArriboImportacion procedure={procedure} />);
  }

  function handleNacionalizacion(procedure) {
    openModal(<FormNacionalizacion procedure={procedure} />);
  }

  function handleImportTracking(procedure) {
    openDialog(<ImportTrackingModal procedure={procedure} />);
  }

  function isConcludedProcedure(procedure) {
    return String(procedure?.stepDetail ?? "")
      .trim()
      .toLowerCase()
      .includes("concluido");
  }

  function handleProcessData(data, params) {
    if (!Array.isArray(data) || !params?.ProcedureStatus) return data;

    return data.filter((procedure) => {
      const isConcluded = isConcludedProcedure(procedure);
      return params.ProcedureStatus === "concluded"
        ? isConcluded
        : !isConcluded;
    });
  }

  // function handleReportDates() {
  //   openDialog(<ReportDatesModal onClose={dialogClose} />);
  // }

  function handleReportPayments() {
    openDialog(<ReportPaymentsModal onClose={dialogClose} />);
  }

  function handleReportRitex() {
    openDialog(<ReportRitexModal onClose={dialogClose} />);
  }

  function handleReportArrival() {
    openDialog(<ReportArrivalModal onClose={dialogClose} />);
  }

  function handleReportIncidents() {
    openDialog(<ReportIncidentsModal onClose={dialogClose} />);
  }

  return (
    <CallServiceTable
      component={({ reload }) => {
        return (
          <div className="flex flex-wrap items-center gap-2">
            {/* <Button
              position="left"
              content="REPORTE DE FECHAS DE ARRIBO"
              onClick={handleReportDates}
              className="px-2 py-2 text-sm leading-5 whitespace-nowrap"
            >
              <i className="fas fa-calendar-alt"></i>
            </Button> */}
            <Button
              position="left"
              content="REPORTES DE PAGOS"
              onClick={handleReportPayments}
              className="px-2 py-2 text-sm leading-5 whitespace-nowrap"
            >
              <i className="fas fa-file-invoice-dollar"></i>
            </Button>
            <Button
              position="left"
              content="REPORTE DE RITEX"
              onClick={handleReportRitex}
              className="px-2 py-2 text-sm leading-5 whitespace-nowrap"
            >
              <i className="fas fa-file-alt"></i>
            </Button>
            <Button
              position="left"
              content="REPORTE DE ARRIBO DE IMPORTACIONES"
              onClick={handleReportArrival}
              className="px-2 py-2 text-sm leading-5 whitespace-nowrap"
            >
              <i className="fas fa-ship"></i>
            </Button>
            <Button
              position="left"
              content="REPORTE DE INCIDENCIAS"
              onClick={handleReportIncidents}
              className="px-2 py-2 text-sm leading-5 whitespace-nowrap"
            >
              <i className="fas fa-exclamation-triangle"></i>
            </Button>
          </div>
        );
      }}
      onClick={handleViewSubProcedure}
      filters={[
        {
          name: "procedureType",
          url: "/procedureType",
          default: "TODOS LOS TRAMITES",
          valueOption: "id",
          labelOption: "name",
        },
        {
          name: "companyId",
          url: "/company/getallcompanies",
          default: "TODAS LAS COMPAÑÍAS",
          valueOption: "id",
          labelOption: "razonSocial",
        },
        {
          name: "ProcedureStatus",
          label: "ESTADOS",
          options: [
            {
              name: "inTransit",
              label: "En tránsito",
            },
            {
              name: "concluded",
              label: "Concluidas",
            },
          ],
        },
      ]}
      urlApi="/procedure/pag"
      addUrl={`fieldnames=codigoDeInterno&fieldnames=proveedor&fieldnames=moneda&fieldnames=totalOrdenDeCompra&fieldnames=terminosPago&fieldnames=naviera&fieldnames=agenteCarga&fieldnames=puertoDeEmbarque(Pol)&fieldnames=puertoDeDestino(Pod)&fieldnames=numeroFactura&fieldnames=etd&fieldnames=eta&fieldnames=diasTransitoMaritimo&fieldnames=documentosOriginales&fieldnames=tránsitoTerrestre&fieldnames=agenteDespachante&fieldnames=fechaDam&fieldnames=nroDim&fieldnames=fechaPagoDeTributos&fieldnames=asignaciónDeCanal&fieldnames=almacénDestino&fieldnames=fechaDeRecepción&fieldnames=pesoBruto&fieldnames=cantidad&fieldnames=siglaContenedor&fieldnames=descripciónGeneralDeLaCompra&fieldnames=medioTransporte&fieldnames=a13ParteModalidadDeTransporte&fieldnames=n°Bl&fieldnames=precinto`}
      addFilters={[
        {
          name: "descripciónGeneralDeLaCompra",
          label: "Producto",
          filter: true,
        },
        {
          name: "codigoDeInterno",
          label: "Nro. Importación",
          filter: true,
        },
        {
          name: "proveedor",
          label: "Proveedor",
          filter: true,
        },
        {
          name: "medioTransporte",
          label: "Modalidad de transporte",
          filter: true,
        },
        {
          name: "naviera",
          label: "Naviera",
          filter: true,
        },
        {
          name: "agenteCarga",
          label: "Agente de carga",
          filter: true,
        },
        {
          name: "puertoDeEmbarque(Pol)",
          label: "POL",
          filter: true,
        },
        {
          name: "puertoDeDestino(Pod)",
          label: "POD",
          filter: true,
        },
        {
          name: "siglaContenedor",
          label: "Sigla de contenedor",
          filter: true,
        },
        {
          name: "n°Bl",
          label: "BL",
          filter: true,
        },
        {
          name: "etd",
          label: "ETD",
          filter: true,
        },
        {
          name: "eta",
          label: "ETA",
          filter: true,
        },
        {
          name: "agenteDespachante",
          label: "Agencia despachante",
          filter: true,
        },
        {
          name: "dimNumber",
          label: "DIM",
          filter: true,
        },
        {
          name: "almacénDestino",
          label: "Almacén de destino",
          filter: true,
        },
        {
          name: "year",
          label: "Gestión",
          filter: true,
        },
      ]}
      clientFilters={["ProcedureStatus"]}
      handleProcessData={handleProcessData}
      header={[
        {
          label: "NÚMERO DE IMPORTACIÓN",
          name: "codigoDeInterno",
          filter: false,
        },
        {
          label: `SUB
          TRÁMITES`,
          name: "subproceso",
          filter: false,
          type: "custom",
          builder: (value, _, data) => (
            <SubProceduresCount procedureId={data.id} />
          ),
        },
        { label: "PROVEEDOR", name: "proveedor", filter: false },
        { label: "PASO ACTUAL", name: "stepDetail" },
        {
          label: "PRODUCTO",
          name: "descripciónGeneralDeLaCompra",
          filter: false,
        },
        /* {
          label: "CANTIDAD (KG)",
          name: "cantidadKg",
          type: "custom",
          builder: (value, _, data) => value || data.pesoBruto || "",
        }, */
        {
          label: "MODALIDAD DE TRANSPORTE",
          name: "a13ParteModalidadDeTransporte",
          type: "custom",
          builder: (value, _, data) =>
            value || data.a13ParteModalidadDeTransporte || "",
        },
        {
          name: "Forms",
          label: "Seguimiento",
          type: "action",
          sticky: true,
          groupActions: true,
          actionGroupClassName: "max-w-[86px]",
          actions: [
            {
              label: "Lista de Pasos",
              icon: "fas fa-shoe-prints",
              action: handleFolderStep,
              color: "text-[#1d4ed8]",
            },
            {
              label: "Seguimiento de Importación",
              icon: "fas fa-book",
              Name: "logbook",
              action: handleFormQuotes,
              color: "text-[#3b82f6]",
              bgColor: "bg-blue-50",
              borderColor: "border-blue-200",
            },
            {
              label: "Fechas de transito y arribo",
              icon: "fa-solid fa-calendar-days",
              Name: "additional-information",
              action: handleAdditionalInformation,
              color: "text-[#8b5cf6]",
              bgColor: "bg-violet-50",
              borderColor: "border-violet-200",
            },
            {
              label: "Bitrix24",
              icon: "fas fa-circle-info",
              Name: "arrival-tracking",
              action: handleArrivalTracking,
              color: "text-[#10b981]",
              bgColor: "bg-emerald-50",
              borderColor: "border-amber-200",
            },
          ],
        },
        {
          name: "Forms",
          label: "Formularios",
          type: "action",
          sticky: true,
          groupActions: true,
          actionGroupClassName: "max-w-[126px]",
          actions: [
            {
              label: "Aplicación del seguro",
              icon: "fa-solid fa-shield-alt",
              Name: "documentation",
              action: handleSureApplication,
              color: "text-[#10b981]",
              bgColor: "bg-emerald-50",
              borderColor: "border-emerald-200",
            },
            {
              label: "Control de pagos",
              icon: "fas fa-dollar-sign",
              Name: "payment-control",
              action: handlePaymentControl,
              color: "text-[#8b5cf6]",
              bgColor: "bg-violet-50",
              borderColor: "border-violet-200",
            },
            {
              /* label: "Información de arribo", */
              label: "Transito",
              icon: "fa-solid fa-ship",
              Name: "arribo-importacion",
              action: handleArriboImportacion,
              color: "text-[#f59e0b]",
              bgColor: "bg-amber-50",
              borderColor: "border-amber-200",
            },
            {
              label: "Seguimiento de Incidencias",
              icon: "fa-solid fa-triangle-exclamation",
              Name: "incidents-tracking",
              action: handleIncidents,
              color: "text-[#ef4444]",
              bgColor: "bg-red-50",
              borderColor: "border-red-200",
            },
            {
              label: "Nacionalización",
              icon: "fa-solid fa-flag",
              Name: "nacionalizacion",
              action: handleNacionalizacion,
              color: "text-[#10b981]",
              bgColor: "bg-emerald-50",
              borderColor: "border-emerald-200",
            },
          ],
        },

        {
          name: "Documents",
          label: "Documentos",
          type: "action",
          sticky: true,
          groupActions: true,
          actionGroupClassName: "max-w-[126px]",
          actions: [
            {
              label: "Seguro",
              icon: "fa-solid fa-file-shield",
              Name: "secure",
              action: handleSecure,
              color: "text-[#7c3aed]",
              bgColor: "bg-violet-50",
              borderColor: "border-violet-200",
            },
            {
              label: "Cuadro comparativo de proveedor",
              icon: "fa-solid fa-chart-column",
              Name: "quote-comparison",
              action: handleQuoteComparison,
              color: "text-[#3b82f6]",
              bgColor: "bg-blue-50",
              borderColor: "border-blue-200",
            },
            // {
            //   label: "Documentación",
            //   icon: "fa-solid fa-folder-open",
            //   Name: "documentation",
            //   action: handleDocument,
            //   color: "text-[#3b82f6]",
            //   bgColor: "bg-blue-50",
            //   borderColor: "border-blue-200",
            // },
            {
              label: "Orden de compra",
              icon: "fa-solid fa-cart-shopping",
              Name: "purchase-order",
              action: handlePurchaseOrder,
              color: "text-[#10b981]",
              bgColor: "bg-emerald-50",
              borderColor: "border-emerald-200",
            },

            {
              label: "Cuadro comparativo de transporte",
              icon: "fa-solid fa-truck-moving",
              Name: "transport-comparison",
              action: handleComparativeTransport,
              hidden: isAirTransportProcedure,
              color: "text-[#f59e0b]",
              bgColor: "bg-amber-50",
              borderColor: "border-amber-200",
            },
            {
              label: "Formulario de contrato de transporte",
              icon: "fa-solid fa-file-contract",
              Name: "transport-contract",
              action: handleTransportContract,
              hidden: isAirTransportProcedure,
              color: "text-[#f59e0b]",
              bgColor: "bg-amber-50",
              borderColor: "border-amber-200",
            },
          ],
        },
      ]}
    />
  );
};

export default FolderList;
